<template>
  <section v-if="show" class="hero" :class="[`hero-${stateTone}`, { 'hero--narrative': narrativeOnly }]">
    <div v-if="!narrativeOnly" class="hero-eyebrow">
      <span class="hero-eyebrow-label">
        <i class="bi bi-stars"></i>
        <span>{{ t('today.hero.headlinesFor', { period: periodText }) }}</span>
      </span>
      <div v-if="canCycle && !narrativeOnly" class="hero-cycler" role="group" :aria-label="t('today.hero.cycle')">
        <button
          type="button"
          class="cycler-btn"
          :aria-label="t('today.hero.prev')"
          @click="prevOpp"
        >
          <i class="bi bi-chevron-left"></i>
        </button>
        <span class="cycler-count">{{ t('today.hero.count', { i: heroIndex + 1, n: totalCandidates }) }}</span>
        <button
          type="button"
          class="cycler-btn"
          :aria-label="t('today.hero.next')"
          @click="nextOpp"
        >
          <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- All-tripwire-only case: no polygon product zones, so engagement /
         conversion / € efficiency math has no input. Render an honest
         "not measurable" state instead of the scary "0% of € potential". -->
    <h2 v-if="!hasMeasurableProductZones || signalNotMeasurable" class="hero-state hero-state-info">
      {{ t('today.hero.onlyDwell1') }}
      <strong>{{ t('today.hero.onlyDwell2') }}</strong>
      {{ t('today.hero.onlyDwell3') }}
      <span class="hero-state-sub">
        {{ t('today.hero.onlyDwellSub1') }} <strong>{{ t('today.hero.onlyDwellSub2') }}</strong> {{ t('today.hero.onlyDwellSub3') }}
      </span>
    </h2>

    <h2 v-else class="hero-state">
      {{ t('today.hero.youreAt') }}
      <span class="hero-pct">{{ pct }}%</span>
      {{ t('today.hero.ofBestWeeks') }}
      <span class="hero-climbcopy">— {{ climbCopy }}</span>.
      <!-- Within-reach € = the canonical store total (Σ per-zone cards) when the
           parent supplies it; else the own-best next-rung gap. -->
      <Tooltip v-if="withinReachFmt && showWithinReach" :text="ladderTooltip">
        <span class="hero-state-sub">
          <strong class="hero-missed">{{ withinReachEurText }}</strong>
          {{ t('today.hero.withinReach') }}{{ reachTarget ? ` ${reachTarget} ` : ' ' }}<span class="hero-state-est">{{ t('today.est') }}</span>
          <i class="bi bi-info-circle hero-state-info"></i>
        </span>
      </Tooltip>
      <!-- Aspire line — proof that ties the headline to real numbers, but its
           tense depends on where you are on the ladder (see aspireMode):
           • climb (below own-best): "you've hit it before" — the target IS your
             own proven best, so past-tense proof motivates the climb back.
           • lead (at/above best-in-class, no gap left): present-tense affirmation —
             you're setting the pace; the best-week figures are your floor.
           • none (past own-best, gap to peers/BIC): dropped — the within-reach line
             already carries the real forward gap, and "hit before" would point at a
             rung you've surpassed, not the unproven one you're now reaching for.
           Engagement = each zone's own past-peak; conversion = admin-set → "(est.)". -->
      <span v-if="topWeeksContext && aspireMode === 'climb'" class="hero-state-aspire">
        <i18n-t keypath="today.hero.aspireClimb" scope="global">
          <template #eng><strong>{{ pctText(topWeeksContext.topWeeksEngagementPct) }}</strong></template>
          <template #conv><strong>{{ pctText(topWeeksContext.zoneAvgConversionPct) }}</strong></template>
        </i18n-t> <span class="hero-state-est">{{ t('today.est') }}</span>.
      </span>
      <span v-else-if="topWeeksContext && aspireMode === 'lead'" class="hero-state-aspire">
        <i18n-t keypath="today.hero.aspireLead" scope="global">
          <template #eng><strong>{{ pctText(topWeeksContext.topWeeksEngagementPct) }}</strong></template>
          <template #conv><strong>{{ pctText(topWeeksContext.zoneAvgConversionPct) }}</strong></template>
        </i18n-t> <span class="hero-state-est">{{ t('today.est') }}</span>.
      </span>
    </h2>

    <button
      v-if="topPriority && hasMeasurableProductZones && !narrativeOnly"
      type="button"
      class="hero-line hero-opp"
      :aria-label="t('today.hero.openIn', { zone: topPriority.zone })"
      @click="$emit('navigate', { tab: 'intelligence-layer', zone: topPriority.zone })"
    >
      <i class="bi bi-bullseye line-ico"></i>
      <span class="line-body">
        <strong class="line-label">{{ opportunityLabel }}</strong>
        <strong class="line-zone">{{ topPriority.zone }}</strong>
        <!-- Strategic-role chip (descriptive context from the Intelligence
             tab's positioning). Sits next to the zone name so the manager
             sees both the zone they're being sent to AND its strategic
             role at a glance. -->
        <Tooltip
          v-if="strategicByZone[topPriority.zone]"
          :text="strategicRoleTooltip(strategicByZone[topPriority.zone])"
        >
          <span
            class="strategic-chip"
            :style="{
              background: mixWhite(strategicByZone[topPriority.zone].color, 0.85),
              color: strategicByZone[topPriority.zone].color,
              borderColor: strategicByZone[topPriority.zone].color
            }"
          >
            {{ strategicByZone[topPriority.zone].icon }}
            {{ strategicByZone[topPriority.zone].label }}
          </span>
        </Tooltip>
        <span class="line-meta">
          <span>{{ t('today.hero.visitors', { n: fmt(topPriority.visitors) }) }}</span>
          <span class="dot">·</span>
          <span>{{ t('today.hero.engaged', { p: fmtPct(topPriority.engagementRate) }) }}</span>
          <span v-if="topPriority.potentialImpact" class="dot">·</span>
          <!-- Consolidated within-reach + lever phrasing. The lever noun
               keeps the lever-eng / lever-conv color so the at-a-glance
               visual signal survives without needing a second pill. -->
          <span v-if="topPriority.potentialImpact" class="line-eur">
            {{ t('today.hero.reachWith', { eur: eurText(topPriority.potentialImpact) }) }}
            <Tooltip
              v-if="topPriority.leverShort"
              :text="focusTip(topPriority)"
            >
              <strong
                class="lever-clause"
                :class="`lever-${topPriority.leverKind}`"
              >{{ t('today.hero.leverPp', { pp: ppText(leverPp(topPriority.leverShort)), lever: leverNoun(topPriority.leverKind) }) }}</strong>
            </Tooltip>
          </span>
        </span>
      </span>
      <i class="bi bi-arrow-right line-arrow"></i>
    </button>

    <button
      v-if="topAction && hasMeasurableProductZones && !narrativeOnly"
      type="button"
      class="hero-line hero-action allen-line"
      :aria-label="t('today.hero.seeIn', { zone: topPriority?.zone || t('today.hero.thisZone') })"
      @click="$emit('navigate', { tab: 'intelligence-layer', zone: topPriority?.zone })"
    >
      <i class="bi bi-lightning-charge-fill line-ico allen-ico"></i>
      <span class="line-body">
        <strong class="line-label">{{ t('today.hero.tryThisWeek') }}</strong>
        <span class="line-text">{{ topAction }}</span>
      </span>
      <!--
        +N more chip — takes the user to Intelligence for the focused
        zone AND scrolls them down to the bottom of that zone's
        deep-dive (where the action library lives). In-place cycling
        between suggestions stays on the ◀ ▶ chevrons in the eyebrow.
      -->
      <span
        v-if="canCycle"
        class="allen-more"
        :aria-label="t('today.hero.seeListIn', { zone: topPriority?.zone || t('today.hero.thisZone') })"
        @click.stop="$emit('navigate', { tab: 'intelligence-layer', zone: topPriority?.zone, scroll: 'bottom' })"
      >
        <i class="bi bi-stars"></i>
        {{ t('today.hero.more', { n: totalCandidates - 1 }) }}
      </span>
      <i class="bi bi-arrow-right line-arrow"></i>
    </button>

    <div v-if="!topPriority && !hasAnyZones" class="hero-fallback">
      {{ t('today.hero.fallbackNoZones') }}
    </div>
    <div v-else-if="!topPriority && hasMeasurableProductZones" class="hero-fallback">
      {{ t('today.hero.fallbackNoOpps') }}
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Tooltip from '@/components/ui/Tooltip.vue'
import { computeStoreEfficiency, computeTopWeeksContext } from '@/prioritization/storeEfficiency'
import { formatNumber, formatEuro, formatPercent } from '@/i18n/format'
import { actionText } from '@/i18n/actions'

// English keeps the exact output it always had; Dutch numbers, € and % go through
// i18n/format.js.
const { t, locale } = useI18n()
const isNl = computed(() => locale.value === 'nl')

const props = defineProps({
  fixedWidgetsData: { type: Object, default: () => ({}) },
  selectedDuration: { type: Number, default: 30 },
  segmentKey: { type: String, default: 'default' },
  // {zoneName → sectorOverride} built once by the parent
  // (FixedWidgets) from the detection-zone store. Forwarded into
  // computeStoreEfficiency so a sector-overridden zone uses its own
  // ceiling in the store-wide efficiency total instead of the
  // company-wide one.
  zoneOverrides: { type: Object, default: () => ({}) },
  // Bubbled up from PrioritizationOpportunities (sorted, highest impact first).
  priorities: { type: Array, default: () => [] },
  hasAnyZones: { type: Boolean, default: false },
  // True when at least one product zone has a polygon (engagement /
  // conversion / € efficiency math has an input). When false, the
  // "0% realized" framing is misleading — we couldn't measure, not
  // measured zero — so we render an honest empty state instead.
  hasMeasurableProductZones: { type: Boolean, default: true },
  // Per-zone dwell {zoneName: { avgSec, confidence }} so the
  // not-measurable hero variant can list the dwell-only zones.
  tripwireDwellMap: { type: Object, default: () => ({}) },
  // Cross-chart strategic-role lookup from the Intelligence tab's
  // composeStrategicPositioning. Shape: { [zoneName]: { icon, label, color } }.
  // When present, the hero's opportunity line shows a small role chip next
  // to the zone name so the manager sees the strategic role of the zone
  // they're being sent to.
  strategicByZone: { type: Object, default: () => ({}) },
  // Today's briefing shows only the store-state narrative — the opportunity/action
  // cards live in the separate "one thing to do" block, so hide them here.
  narrativeOnly: { type: Boolean, default: false },
  // The canonical store within-reach €/mo (Σ per-zone potentialImpact). When
  // provided, it drives the hero's "€ within reach" so the headline equals the
  // sum of the cards below. Falls back to the own-best climb gap when absent.
  storeWithinReachEur: { type: Number, default: null },
  // False → the headline omits its "€ within reach" clause (a host that shows the
  // same € elsewhere, e.g. Today v2's Store-health row, so it isn't stated twice).
  showWithinReach: { type: Boolean, default: true },
})

defineEmits(['navigate'])

const show = computed(() => props.hasAnyZones)

const efficiency = computed(() =>
  computeStoreEfficiency(props.fixedWidgetsData, props.segmentKey, props.zoneOverrides, props.fixedWidgetsData?._cohort ?? null)
)
// Re-anchored to the four-rung ladder (matches the ATTRACT gauge): the hero %
// is the own-best headline (100% = your best weeks), and "within reach" is the
// gap to the NEXT rung you haven't reached — not the full best-in-class gap.
const pct = computed(() => Math.round(efficiency.value.headlinePct || 0))
// platform_capabilities.md §17.7 — the backend's own "engagement not measurable" verdict for a footfall
// (all-tripwire) store. Redundant with hasMeasurableProductZones but never weakens it.
const signalNotMeasurable = computed(() => efficiency.value.notMeasurable === true)

const RUNG_NAME = { best: 'your best weeks', peers: 'your peers', bic: 'best-in-class' }
const activeRung = computed(() => {
  const t = efficiency.value
  const hp = t.headlinePct || 0
  const rungs = [{ key: 'best', pct: 100, total: t.ownBestTotal }]
  if (t.peerTickPct != null) rungs.push({ key: 'peers', pct: t.peerTickPct, total: t.cohortTotal })
  if (t.ceilingTickPct != null) rungs.push({ key: 'bic', pct: t.ceilingTickPct, total: t.ceilingTotal })
  const next = rungs.find(r => r.pct > hp + 2) || null
  return { hp, rungs, next, realized: t.realizedTotal || 0 }
})
// The clause after the dash — "peak" is a fresh synonym for the own-best anchor so
// we don't repeat "best weeks"; it flips to the higher rung once you're past it.
const climbCopy = computed(() => {
  const { hp, next } = activeRung.value
  if (!next) return t('today.hero.climb.leading')
  if (next.key === 'best') {
    if (hp >= 88) return t('today.hero.climb.almost')
    if (hp >= 60) return t('today.hero.climb.climbing')
    return t('today.hero.climb.room')
  }
  // AT your own best (within the same ±2 the ATTRACT gauge uses for "right at your best
  // weeks") is not PAST it — the two must never disagree on the same number.
  const nextName = t(next.key === 'peers' ? 'today.hero.climb.peersNext' : 'today.hero.climb.bicNext')
  return t(Math.abs(hp - 100) <= 2 ? 'today.hero.climb.atPeak' : 'today.hero.climb.pastPeak', { next: nextName })
})
// The € line's target phrase: "back there" = the own-best peak; a named rung once higher.
const reachTarget = computed(() => {
  // With the canonical store total, the € spans every zone climbing its OWN next
  // rung, so a single store-level rung clause ("to reach your peers") would
  // mislabel it — drop the clause and let the number stand.
  if (props.storeWithinReachEur != null) return ''
  const n = activeRung.value.next
  if (!n) return ''
  if (n.key === 'best') return t('today.hero.target.back')
  return t(n.key === 'peers' ? 'today.hero.target.peers' : 'today.hero.target.bic')
})
// Which aspire line (part 3) to show — see the template comment. 'climb' while
// climbing back to your OWN best (a hit-before target); 'lead' at the very top
// (no gap, present-tense affirmation); 'none' once past own-best but still below
// peers/BIC, where the within-reach line already carries the real forward gap.
const aspireMode = computed(() => {
  const n = activeRung.value.next
  if (!n) return 'lead'
  if (n.key === 'best') return 'climb'
  return 'none'
})

const climbEuro = computed(() => {
  const { next, realized } = activeRung.value
  return next ? Math.max(0, (next.total || 0) - realized) : 0
})
// Prefer the canonical store within-reach € (Σ per-zone cards) when supplied, so
// the headline equals the sum of the opportunities below it; fall back to the
// own-best climb gap for any caller that doesn't pass it.
const withinReachEur = computed(() =>
  props.storeWithinReachEur != null ? props.storeWithinReachEur : climbEuro.value
)
const withinReachFmt = computed(() => {
  const v = Math.round(withinReachEur.value || 0)
  return v > 0 ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : ''
})
// "€1,234" (English, as before) / "€ 1.234".
const withinReachEurText = computed(() =>
  isNl.value ? formatEuro(Math.round(withinReachEur.value || 0), { lang: 'nl' }) : `€${withinReachFmt.value}`)

// Full sector label for prose. Matches the dropdown in Settings →
// Company Benchmark so the hero reads with the same name the admin
// configured (e.g. "DIY / Home Improvement", not "DIY").
const SECTOR_LABELS = {
  default:      'your sector',
  grocery:      'Grocery / FMCG',
  diy:          'DIY / Home Improvement',
  furniture:    'Furniture / Garden / Consumer Electronics',
  fashion:      'Fashion / Apparel',
  discount:     'Discount / Value',
  pharmacy:     'Pharmacy / Drugstore',
  specialty:    'Specialty Retail',
  large_format: 'Large Format / Big Box',
}

// Sector label used after "based on " in the hero. Full sector
// name — manager sees their configured sector reflected verbatim.
const provenancePhrase = computed(() =>
  SECTOR_LABELS[props.segmentKey] || SECTOR_LABELS.default
)

// Top-weeks descriptive context — uses PastPeakEngagement_p95 (the
// 95th percentile of weekly engagement rates, written nightly by
// zone_calibration.py) as the "your past peak" reference.
const topWeeksContext = computed(() =>
  computeTopWeeksContext(props.fixedWidgetsData, props.zoneOverrides)
)

const ladderTooltip = computed(() => t('today.hero.ladderTip'))

const stateTone = computed(() => {
  const p = pct.value
  if (p >= 90) return 'good'
  if (p >= 60) return 'mid'
  return 'low'
})

// Same phrases as storeEfficiency.periodLabel, in the reader's language.
const PERIODS = [1, 7, 30, 90, 365, 1000, 2000, 3000, 4000]
const periodText = computed(() => (PERIODS.includes(props.selectedDuration)
  ? t(`today.hero.period.${props.selectedDuration}`)
  : t('today.hero.period.other', { n: props.selectedDuration })))

// Top opportunities the user can cycle through (up to 3). Wrong-pick risk
// drops sharply when the user can flip through alternatives instead of just
// trusting one verdict.
const candidates = computed(() => {
  const list = props.priorities || []
  // Prefer non-muted (open opportunities) but fall back to any with € impact.
  const open = list.filter(p => !p.muted && p.potentialImpact > 0)
  const any = list.filter(p => p.potentialImpact > 0)
  return (open.length ? open : any).slice(0, 3)
})

const heroIndex = ref(0)
watch(
  () => candidates.value.map(c => c.zone).join('|'),
  () => { heroIndex.value = 0 }
)

const topPriority = computed(() => candidates.value[heroIndex.value] || null)

const totalCandidates = computed(() => candidates.value.length)
const canCycle = computed(() =>
  props.hasMeasurableProductZones && totalCandidates.value > 1
)

function nextOpp() {
  if (!canCycle.value) return
  heroIndex.value = (heroIndex.value + 1) % totalCandidates.value
}
function prevOpp() {
  if (!canCycle.value) return
  heroIndex.value = (heroIndex.value - 1 + totalCandidates.value) % totalCandidates.value
}

// Label changes with cycler position so #2 and #3 don't claim to be the biggest.
const opportunityLabel = computed(() => {
  if (heroIndex.value === 0) return t('today.hero.oppBiggest')
  if (heroIndex.value === 1) return t('today.hero.oppNext')
  return t('today.hero.oppAlso')
})

// First quick-win action for that zone.
const topAction = computed(() => {
  const a = topPriority.value?.actions?.[0]
  if (!a) return null
  // The library's English text is data; the reader sees it in their language.
  return (a.text && actionText(a.text)) || a.label || null
})

function fmt(n){
  const v = Number(n || 0)
  if (isNl.value) return formatNumber(v, { lang: 'nl' })
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 })
}
function fmtPct(rate){
  const v = Number(rate || 0)
  if (isNl.value) return formatPercent(v * 100, { decimals: 1, lang: 'nl' })
  return `${(v * 100).toFixed(1)}%`
}
function fmtMoney(n){
  const v = Math.round(Number(n || 0))
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 })
}
// "€1,234" (English, as before) / "€ 1.234".
function eurText(n){
  if (isNl.value) return formatEuro(Math.round(Number(n || 0)), { lang: 'nl' })
  return `€${fmtMoney(n)}`
}
// A percentage the code already rounded (38.2 -> "38.2%" / "38,2%").
function pctText(v){
  return `${isNl.value ? String(v).replace('.', ',') : v}%`
}
// Percentage points: "5" stays "5" (the message adds "pp" / "%-punt"); Dutch comma.
function ppText(pp){
  return isNl.value ? String(pp).replace('.', ',') : pp
}
// The lever tooltip. English: "Focus: <bestLever>" exactly as before.
function focusTip(p){
  if (!isNl.value) return `Focus: ${p.bestLever}`
  const noun = leverNoun(p.leverKind)
  return noun ? t('today.hero.focus', { lever: noun }) : `Focus: ${p.bestLever}`
}

// Helpers for the consolidated within-reach + lever sentence.
// classifier.computePotentialEUR encodes the pp value into leverShort
// (e.g. "Engagement +5pp"); pulling the number back out keeps the sentence
// in sync with the underlying math without re-deriving it here.
function leverPp(leverShort){
  if (!leverShort) return ''
  const m = String(leverShort).match(/\+?(\d+(?:\.\d+)?)pp/i)
  return m ? m[1] : ''
}
function leverNoun(leverKind){
  if (['eng', 'conv', 'reach'].includes(leverKind)) return t(`today.hero.lever.${leverKind}`)
  return ''
}

// Lightweight color-mix for the strategic-chip background tint. Inline-
// applied via :style because the chip color is dynamic (one of the four
// QUADRANT_META colors from the Intelligence-tab composer).
function mixWhite(hex, weight = 0.85){
  if (!hex || hex[0] !== '#' || hex.length !== 7) return '#fff'
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const mix = (c) => Math.round(c + (255 - c) * weight)
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`
}

// Tooltip text for the strategic-role chip — same content shown on
// the Prio cards. Combines the role's short definition (legend) and
// the "what to do about it" sentence (interp) into a multi-line
// hover. Matches the Strategic Positioning chart's legend strip
// wording so the same role reads identically wherever it appears.
function strategicRoleTooltip(role){
  if (!role) return ''
  const lines = []
  if (role.legend) lines.push(role.legend)
  if (role.interp) lines.push(role.interp)
  return lines.join('\n\n')
}
</script>



<style scoped>
.hero{
  position: relative;
  background: #fff;
  border: 1px solid #e6edf3;
  border-left: 6px solid var(--accent, #36910d);
  border-radius: 16px;
  padding: 28px 32px 26px;
  margin: 8px 0 32px;
  box-shadow: 0 8px 22px -12px rgba(13,38,76,0.14);
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.hero-good{ --accent: #36910d; }
.hero-mid { --accent: #D3A741; }
.hero-low { --accent: #AD3935; }
/* Narrative-only (inside the Today Allen-briefing card) — drop the card chrome so
   the store-state line reads as plain text under the single Allen header. */
.hero--narrative{
  background: none;
  border: none;
  border-left: none;
  border-radius: 0;
  padding: 0;
  margin: 0;
  box-shadow: none;
  gap: 8px;
}

.hero-eyebrow{
  display: flex; align-items: center; gap: 6px;
  font-size: .72rem;
  text-transform: uppercase;
  letter-spacing: .8px;
  color: var(--accent);
  font-weight: 700;
  margin-bottom: 2px;
}
/* keep the icon glued to its label so they wrap as one unit */
.hero-eyebrow-label{
  display: inline-flex; align-items: center; gap: 6px;
  min-width: 0;
}

.hero-cycler{
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: color-mix(in srgb, var(--accent) 8%, #fff);
  border: 1px solid color-mix(in srgb, var(--accent) 25%, #e6edf3);
  border-radius: 999px;
  padding: 2px 4px;
  text-transform: none;
  letter-spacing: 0;
  color: #425461;
}
.cycler-btn{
  appearance: none;
  background: transparent;
  border: 0;
  width: 22px; height: 22px;
  border-radius: 999px;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
  color: var(--accent);
  font-size: .75rem;
  transition: background-color .15s ease;
}
.cycler-btn:hover{ background: color-mix(in srgb, var(--accent) 18%, #fff); }
.cycler-btn:focus-visible{
  outline: 2px solid color-mix(in srgb, var(--accent) 55%, #fff);
  outline-offset: 1px;
}
.cycler-count{
  font-size: .68rem;
  font-weight: 700;
  color: #425461;
  padding: 0 4px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;        /* "1 of 3" never breaks across lines */
}

/* When the row gets tight, stack the eyebrow: cycler pill on its own line,
   left-aligned, above the label — so neither the label nor the "x of N"
   count get squeezed/wrapped inside the inline row.
   NOTE: placed AFTER the base .hero-cycler rule so its margin-left:0 actually
   overrides the base margin-left:auto (media queries add no specificity). */
@media (max-width: 900px){
  .hero-eyebrow{
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .hero-cycler{
    margin-left: 0;     /* left-aligned, not pushed right */
    order: -1;          /* place the pill above the label */
  }
}

.hero-state{
  margin: 0 0 4px;
  font-size: 1.5rem;
  line-height: 1.35;
  color: #1a2a36;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.hero-pct{
  color: var(--accent);
  font-weight: 800;
}
/* Ladder-aware copy right after the % ("— almost at your best weeks") — a lighter
   continuation of the headline, not a competing figure. */
.hero-climbcopy{
  font-weight: 600;
  color: #556372;
}
.hero-state-sub{
  display: block;
  font-size: 1rem;
  color: #425461;
  font-weight: 400;
  margin-top: 8px;
}
.hero-missed{
  /* Growth Potential text in the hero — amber, not red. Opportunity, not loss. */
  color: #A56B1F;
  font-weight: 700;
}
.hero-state-sub{
  cursor: help;
}
.hero-state-info{
  font-size: .8rem;
  opacity: .55;
  margin-left: 2px;
  vertical-align: baseline;
}

/* "Your top weeks averaged …" descriptive line under the primary
   within-reach line. Smaller + muted so it reads as a follow-on, not
   a competing number. Uses a quieter blue tone to differentiate
   visually from the amber Growth Potential figure above. */
.hero-state-aspire{
  display: block;
  font-size: .9rem;
  color: #6b7a87;
  font-weight: 400;
  margin-top: 4px;
}
.hero-state-aspire strong{
  color: #4F7EB3;
  font-weight: 700;
}
/* Muted "(est.)" marker — flags that the € figure rests on the
   admin-configured conversion / avg-value (no POS connection in the
   default deployment). */
.hero-state-est{
  font-size: .75rem;
  color: #9aa7b3;
  font-style: italic;
  font-weight: 400;
  margin-left: 2px;
}

.hero-line{
  appearance: none;
  background: color-mix(in srgb, var(--accent) 5%, #fff);
  border: 1px solid color-mix(in srgb, var(--accent) 25%, #e6edf3);
  border-radius: 12px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  text-align: left;
  color: #1a2a36;
  transition: background-color .15s ease, transform .05s ease, border-color .15s ease;
}
.hero-line:hover{
  background: color-mix(in srgb, var(--accent) 12%, #fff);
  border-color: color-mix(in srgb, var(--accent) 45%, #e6edf3);
}
.hero-line:active{ transform: translateY(1px) }
.hero-line:focus-visible{
  outline: 2px solid color-mix(in srgb, var(--accent) 55%, #fff);
  outline-offset: 2px;
}

/* Allen-voiced action line — slightly different visual to feel like a colleague's nudge */
.hero-line.allen-line{
  background: linear-gradient(90deg, color-mix(in srgb, #4F7EB3 10%, #fff) 0%, color-mix(in srgb, var(--accent) 5%, #fff) 100%);
  border-color: color-mix(in srgb, #4F7EB3 30%, #e6edf3);
}
.hero-line.allen-line:hover{
  background: linear-gradient(90deg, color-mix(in srgb, #4F7EB3 18%, #fff) 0%, color-mix(in srgb, var(--accent) 12%, #fff) 100%);
  border-color: color-mix(in srgb, #4F7EB3 50%, #e6edf3);
}
.allen-ico{
  color: #4F7EB3 !important;
}
.allen-more{
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  margin-left: 8px;
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .2px;
  color: #4F7EB3;
  background: color-mix(in srgb, #4F7EB3 10%, #fff);
  border: 1px solid color-mix(in srgb, #4F7EB3 35%, #fff);
  border-radius: 999px;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, transform .05s ease;
  white-space: nowrap;
  flex-shrink: 0;
}
.allen-more:hover{
  background: color-mix(in srgb, #4F7EB3 22%, #fff);
  border-color: #4F7EB3;
}
.allen-more:active{ transform: translateY(1px) }
.allen-more i{ font-size: .72rem; }

.line-ico{
  font-size: 1.05rem;
  color: var(--accent);
  flex-shrink: 0;
}
.line-body{
  flex: 1 1 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.line-label{
  font-weight: 700;
  color: #425461;
  flex-shrink: 0;
}
.line-zone{
  color: var(--accent);
  font-weight: 700;
}
.line-meta{
  color: #6b7a86;
  font-size: .9rem;
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
}
.line-meta .dot{ opacity: .55; }
.line-eur{ color: #1a2a36; font-weight: 600; }

/* Lever clause inline inside the within-reach sentence. Keeps the same
   blue/olive at-a-glance signal the .lever-pill carried, without a
   second visual element. Pairs with the consolidated phrasing
   "≈ €X within reach with +Ypp <lever-clause>". */
.lever-clause{
  font-weight: 700;
  white-space: nowrap;
}
.lever-clause.lever-eng{ color: #4F7EB3; }
.lever-clause.lever-conv{ color: #6F7C2A; }
.lever-clause.lever-reach{ color: #3E8C80; }

/* Strategic-role chip — sits beside the zone name on the hero. Borrowed
   vocabulary from the Intelligence tab's positioning chart so the user
   sees both "what to do" and "what role this zone plays" without
   leaving the hero. */
.strategic-chip{
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 9px;
  margin-left: 4px;
  border: 1px solid;
  border-radius: 999px;
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .2px;
  white-space: nowrap;
}

.lever-pill{
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .2px;
  margin-left: 6px;
  white-space: nowrap;
}
.lever-pill i{ font-size: .72rem; }
.lever-pill.lever-eng{
  color: #4F7EB3;
  background: color-mix(in srgb, #4F7EB3 12%, #fff);
  border: 1px solid color-mix(in srgb, #4F7EB3 35%, #fff);
}
.lever-pill.lever-conv{
  color: #6F7C2A;
  background: color-mix(in srgb, #B2C149 18%, #fff);
  border: 1px solid color-mix(in srgb, #B2C149 55%, #fff);
}
.line-text{
  color: #1a2a36;
  font-weight: 500;
}
.line-arrow{
  color: var(--accent);
  font-size: 1rem;
  opacity: .6;
  transition: opacity .15s ease, transform .15s ease;
  flex-shrink: 0;
}
.hero-line:hover .line-arrow{ opacity: 1; transform: translateX(2px); }

.hero-fallback{
  color: #6b7a86;
  font-style: italic;
  font-size: .9rem;
}
</style>
